import Modal from '@/components/common/Modal'
import ButtonIcon from '@/components/common/ButtonIcon'
import styled from 'styled-components'
import Row from '@/components/common/Row'
import Text from '@/components/common/Text'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { close } from '@/store/modalsSlice'
import { device } from '@/common/constants/device'
import { userApi } from '@/common/services/api'
import getConfig from 'next/config'
import baseLogger from '@/common/services/BaseLogger'
import { addNotification } from '@/store/notificationsSlice'
import { getCookie, setCookie } from 'cookies-next'
import Stepper from '@/components/common/Stepper'
import StepOne from '@/components/FeedbackModal/StepOne'
import StepTwo from '@/components/FeedbackModal/StepTwo'
import StepThree from '@/components/FeedbackModal/StepThree'
import { useTranslations } from 'next-intl'

const FeedbackModalBlock = styled.div`
  padding: 0 16px;

  @media ${device.tablet} {
    padding: 0 24px;
  }

  .premium-modal-email {
    margin-bottom: 16px;
  }
`
const FeedbackModalContent = styled.div`
  position: relative;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 100%;
`

const FeedbackModalWrap = styled(Modal)`
  .modal-content {
    width: calc(100% - 30px);

    @media ${device.tablet} {
      width: 100%;
    }
  }
`

const FeedbackModalTop = styled(Row)`
  justify-content: space-between;
  column-gap: 12px;
  padding: 0 16px;

  @media ${device.tablet} {
    padding: 0 24px;
    align-items: center;
  }
`

const FeedbackModalClose = styled(ButtonIcon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-color: var(--tertiary);
  transition: transform 0.26s ease-in-out;
  flex-shrink: 0;

  &:hover {
    transform: rotate(90deg);
  }

  @media ${device.tablet} {
    width: 24px;
    height: 24px;
  }
`

const FeedbackModal = ({ isRedirect, survey = 'search' }) => {
  const [isLoading, setLoading] = useState(false)
  const t = useTranslations('creatives')
  const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    experience: null,
    searchRating: null,
    weakestPoint: null,
    weakestPointContent: null,
    aboutProduct: null,
    aboutProductContent: null,
    improvementContent: null,
  })

  const handleNextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1)
  }

  const updateSurveyCookie = () => {
    const COOKIE_NAME = 'surveyStates'
    const COOKIE_MAX_AGE = 30 * 24 * 60 * 60
    const currentData = JSON.parse(getCookie(COOKIE_NAME) || '{}')
    const updatedData = {
      ...currentData,
      survey1: { state: 'closed', closedAt: new Date().getTime() },
      search_v2: { state: 'closed', closedAt: new Date().getTime() },
    }
    setCookie(COOKIE_NAME, JSON.stringify(updatedData), {
      maxAge: COOKIE_MAX_AGE,
    })
  }

  const redirectToTeasers = () => {
    const BASE_DOMAIN = getConfig().publicRuntimeConfig.baseDomain
    window.location.href = `https://${BASE_DOMAIN}/teasers`
  }

  const closeModal = async (sendRequest = true) => {
    const isFormDataEmpty = () =>
      Object.values(formData).every((value) => value === null)

    updateSurveyCookie()
    dispatch(close())

    if (sendRequest && !isFormDataEmpty()) {
      await sendFeedbackRequest()
    }

    if (isRedirect) {
      redirectToTeasers()
    }
  }

  const sendFeedbackRequest = async () => {
    try {
      setLoading(true)

      const payload = {
        survey: survey,
        responses: [
          formData.experience && {
            question: 'usage_experience',
            answers: [String(formData.experience)],
          },
          formData.searchRating && {
            question: 'search_rating',
            answers: [String(formData.searchRating)],
          },
          formData.weakestPoint && {
            question: 'weakest_point',
            answers: formData.weakestPoint.select,
            comment: formData.weakestPointContent
              ? formData.weakestPointContent
              : undefined,
          },
          formData.aboutProduct && {
            question: 'about_product',
            answers: formData.aboutProduct.select,
            comment: formData.aboutProductContent
              ? formData.aboutProductContent
              : undefined,
          },
          formData.improvementContent && {
            question: 'improvement_content',
            answers: [formData.improvementContent],
          },
        ].filter(Boolean),
      }

      await userApi.setFeedback({
        data: payload,
      })

      dispatch(
        addNotification({
          type: 'success',
          text: `common.feedbackNotification`,
        })
      )
    } catch (e) {
      baseLogger.logger.error(e, 'feedback form error')
    } finally {
      setLoading(false)
    }
  }

  const onClickButtonHandler = async () => {
    await sendFeedbackRequest()
    closeModal(false)
  }

  const renderStepContent = () => {
    return (
      <>
        <Stepper
          totalSteps={3}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
        {currentStep === 1 && (
          <StepOne
            formData={formData}
            setFormData={setFormData}
            handleNextStep={handleNextStep}
          />
        )}
        {currentStep === 2 && (
          <StepTwo
            formData={formData}
            setFormData={setFormData}
            handleNextStep={handleNextStep}
          />
        )}
        {currentStep === 3 && (
          <StepThree
            formData={formData}
            setFormData={setFormData}
            onClickButtonHandler={onClickButtonHandler}
            closeModal={closeModal}
            isLoading={isLoading}
          />
        )}
      </>
    )
  }

  return (
    <FeedbackModalWrap maxWidth="644" needOffsetHeader onClose={closeModal}>
      <FeedbackModalContent>
        <FeedbackModalTop>
          <Text size="20" color="black" weight="bold">
            {t('helpUsImprove')}
          </Text>
          <FeedbackModalClose
            onClick={closeModal}
            icon="cross"
            width={3}
            height={3}
          />
        </FeedbackModalTop>
        <FeedbackModalBlock>{renderStepContent()}</FeedbackModalBlock>
      </FeedbackModalContent>
    </FeedbackModalWrap>
  )
}

export default FeedbackModal
